import * as React from 'react';
import { reviewAccountOrganisation, type AccountOrganisationReviewResult, type ReviewTenderPreparationAccountBody } from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  open: boolean;
  allowed: boolean;
  disabled: boolean;
  ownerKey: string;
  formKey: string;
  tenantId?: string;
  entityId?: string;
  values: ReviewTenderPreparationAccountBody;
  onApply: (values: Partial<ReviewTenderPreparationAccountBody>) => void;
}
const fieldLabels = {
  industry: 'Industry', organisationType: 'Organisation type', size: 'Organisation size',
  description: 'Description', phone: 'Phone', email: 'Email', addressLine1: 'Address',
  addressLine2: 'Address line 2', city: 'City', stateProvince: 'State or province', postcode: 'Postcode', country: 'Country'
} satisfies Record<AccountOrganisationReviewResult['suggestions'][number]['field'], string>;
const directFields = ['industry', 'phone', 'email', 'addressLine1', 'city', 'stateProvince', 'postcode', 'country'] as const;
type DirectField = typeof directFields[number];
function isDirectField(field: string): field is DirectField {
  return directFields.some(value => value === field);
}
const originLabel = (origin: 'website' | 'general_ai') => origin === 'website' ? 'Website evidence' : 'General AI suggestion — unsourced and unverified';

/** Review, deliberate draft application and canonical save are separate actions. */
export function AccountOrganisationReview({ open, allowed, disabled, ownerKey, formKey, tenantId, entityId, values, onApply }: Props) {
  const [result, setResult] = React.useState<AccountOrganisationReviewResult>();
  const [suggestions, setSuggestions] = React.useState<string[]>([]);
  const [summary, setSummary] = React.useState<string[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const generation = React.useRef(0);
  const controller = React.useRef<AbortController | undefined>(undefined);
  const name = values.name.trim(), website = values.website?.trim() ?? '';
  const identity = JSON.stringify([ownerKey, formKey, tenantId, entityId, name, website]);
  React.useLayoutEffect(() => {
    generation.current++;
    controller.current?.abort(); controller.current = undefined;
    setBusy(false); setResult(undefined); setSelected([]); setError('');
    return () => { generation.current++; controller.current?.abort(); controller.current = undefined; };
  }, [identity, open, allowed]);

  async function review() {
    if (!open || !allowed || disabled || controller.current) return;
    let url: URL;
    try {
      url = new URL(website);
      if (url.protocol !== 'https:' || url.username || url.password || url.port || url.pathname !== '/' || url.search || url.hash) throw new Error();
    } catch {
      setError('Enter a public HTTPS website, such as https://example.com, without a page path or query.'); return;
    }
    if (name.length < 3) { setError('Enter a public organisation name of at least three characters.'); return; }
    const token = ++generation.current, abort = new AbortController(), requestId = crypto.randomUUID();
    controller.current = abort;
    setBusy(true); setError(''); setResult(undefined); setSelected([]);
    try {
      const next = await reviewAccountOrganisation({ requestId, name, website: url.href }, { signal: abort.signal });
      if (generation.current !== token || abort.signal.aborted) return;
      if (next.requestId !== requestId || next.tenantId !== tenantId || next.entityId !== entityId ||
          next.name !== name || next.website !== url.href) {
        setError('This review belongs to a different account context. Your draft has not changed.'); return;
      }
      setResult(next); setSuggestions(next.suggestions.map(item => item.value)); setSummary(next.summary.map(item => item.text));
    } catch (cause) {
      if (generation.current === token && !abort.signal.aborted) setError(cause instanceof Error ? cause.message : 'Organisation review is unavailable. Try again or continue manually.');
    } finally {
      if (generation.current === token) { controller.current = undefined; setBusy(false); }
    }
  }
  const toggle = (id: string, checked: boolean) => setSelected(current => checked ? [...current.filter(value => value !== id), id] : current.filter(value => value !== id));
  function apply() {
    if (!result || !open || !allowed || disabled || busy) return;
    const patch: Partial<ReviewTenderPreparationAccountBody> = {};
    const notes: string[] = [], sourceIds = new Set<string>(), chosenFields = new Set<string>();
    const replacements: string[] = [];
    for (const [index, item] of result.suggestions.entries()) {
      if (!selected.includes('field-' + index) || !suggestions[index]?.trim()) continue;
      if (chosenFields.has(item.field)) { setError('Select only one suggestion for each account field.'); return; }
      chosenFields.add(item.field);
      const text = suggestions[index]!.trim();
      if (isDirectField(item.field)) {
        const limit = item.field === 'phone' ? 50 : item.field === 'industry' ? 100 : 300;
        if (text.length > limit) { setError(fieldLabels[item.field] + ' must be at most ' + limit + ' characters. Edit the suggestion before applying.'); return; }
        patch[item.field] = text;
        if (values[item.field]?.trim() && values[item.field]!.trim() !== text) replacements.push(fieldLabels[item.field] + ': ' + values[item.field]);
      } else notes.push(fieldLabels[item.field] + ': ' + text + ' [' + originLabel(item.origin) + (text !== item.value ? '; edited by staff' : '') + ']');
      if (!isDirectField(item.field)) item.sourceIds.forEach(id => sourceIds.add(id));
    }
    for (const [index, item] of result.summary.entries()) {
      const text = summary[index]?.trim();
      if (!selected.includes('summary-' + index) || !text) continue;
      notes.push(text + ' [' + originLabel(item.origin) + (text !== item.text ? '; edited by staff' : '') + ']');
      item.sourceIds.forEach(id => sourceIds.add(id));
    }
    if (notes.length) {
      const sources = result.sources.filter(source => sourceIds.has(source.id))
        .map(source => 'Source: ' + source.url + ' (retrieved ' + source.retrievedAt + ')');
      const block = ['Organisation review (' + result.reviewedAt + ')', ...notes, ...sources].join('\n');
      patch.notes = [values.notes?.trim(), block].filter(Boolean).join('\n\n');
      if (patch.notes.length > 5000) { setError('These selections would exceed the 5,000-character notes limit. Shorten the summary or select fewer entries. Existing notes have not changed.'); return; }
    }
    if (!Object.keys(patch).length) return;
    if (replacements.length && !window.confirm('Replace these current draft values with the selected suggestions?\n\n' + replacements.join('\n'))) return;
    onApply(patch); setSelected([]); setError('');
  }
  const checkbox = (id: string, label: string) => <label className="flex items-start gap-2"><input type="checkbox" checked={selected.includes(id)} onChange={event => toggle(id, event.target.checked)} />{label}</label>;
  return <section aria-label="Organisation review" className="space-y-3 rounded-md border p-3 text-sm"
    onKeyDown={event => { if (event.key === 'Enter' && event.target instanceof HTMLInputElement) event.preventDefault(); }}>
    <p className="font-medium">Review organisation</p>
    <p className="text-muted-foreground">Uses only this public name and website plus general AI knowledge. Check the sources and identity before applying anything. Review uses the configured AI allowance; retrying starts a new attempt.</p>
    {!allowed && <p role="status">Organisation review is unavailable with your current access. Continue manually.</p>}
    <div className="flex flex-wrap gap-2">
      <Button type="button" variant="outline" disabled={!allowed || disabled || busy} onClick={() => void review()}>{busy ? 'Reviewing organisation…' : 'Review organisation'}</Button>
      {busy && <Button type="button" variant="outline" onClick={() => {
        generation.current++; controller.current?.abort(); controller.current = undefined; setBusy(false);
        setError('Review cancelled. No results were applied. The attempt may still use AI allowance.');
      }}>Cancel organisation review</Button>}
    </div>
    {error && <p role="status">{error} Your manual account draft is retained.</p>}
    {result && <fieldset disabled={disabled || !allowed} className="space-y-3">
      <legend className="font-medium">Select and edit suggestions for your draft</legend>
      <p>Nothing is saved by applying. Notes are appended, preserving your existing notes. Edited text is your draft, not verified evidence.</p>
      {(['website', 'general_ai'] as const).map(origin => <section key={origin} aria-label={originLabel(origin)} className="space-y-3 rounded-md border p-2">
        <h4 className="font-medium">{originLabel(origin)}</h4>
        {result.suggestions.map((item, index) => item.origin === origin && <div key={item.field + ':' + item.origin} className="space-y-1">
          {checkbox('field-' + index, 'Apply ' + fieldLabels[item.field] + ' (' + (origin === 'website' ? 'website' : 'general AI') + ')')}
          <Label htmlFor={'organisation-field-' + index}>{fieldLabels[item.field]} suggestion ({origin === 'website' ? 'website' : 'general AI'})</Label>
          <Input id={'organisation-field-' + index} value={suggestions[index] ?? ''} maxLength={item.field === 'description' ? 2000 : 300}
            onChange={event => setSuggestions(current => current.map((value, i) => i === index ? event.target.value : value))} />
          <p className="whitespace-pre-wrap break-words text-muted-foreground">Original: {item.value}</p>
          {item.sourceIds.map(id => <p key={id}>Evidence: {id}</p>)}
          {!isDirectField(item.field) && <p className="text-muted-foreground">Adds to Notes.</p>}
        </div>)}
        {result.summary.map((item, index) => item.origin === origin && <div key={item.origin + ':' + item.text + ':' + item.sourceIds.join(',')} className="space-y-1">
          {checkbox('summary-' + index, 'Add summary ' + (index + 1) + ' (' + (origin === 'website' ? 'website' : 'general AI') + ') to Notes')}
          <Label htmlFor={'organisation-summary-' + index}>Summary {index + 1} ({origin === 'website' ? 'website' : 'general AI'})</Label>
          <Textarea id={'organisation-summary-' + index} value={summary[index] ?? ''} maxLength={2000}
            onChange={event => setSummary(current => current.map((value, i) => i === index ? event.target.value : value))} />
          <p className="whitespace-pre-wrap break-words text-muted-foreground">Original: {item.text}</p>
          {item.sourceIds.map(id => <p key={id}>Evidence: {id}</p>)}
        </div>)}
      </section>)}
      <section aria-label="Retrieved website sources" className="space-y-2">
        <h4 className="font-medium">Retrieved website sources</h4>
        {result.sources.map(source => <details key={source.id} className="rounded-md border p-2">
          <summary>{source.id}: {source.title || source.url}</summary>
          <a href={source.url} target="_blank" rel="noreferrer" className="break-all underline">View source: {source.url}</a>
          <p>Retrieved {source.retrievedAt}</p><p className="whitespace-pre-wrap break-words">{source.excerpt}</p>
        </details>)}
      </section>
      <p>Not found: {result.missingFields.map(field => fieldLabels[field]).join(', ') || 'None reported'}</p>
      {result.limitations.map(text => <p key={text} className="text-muted-foreground">{text}</p>)}
      <Button type="button" variant="outline" disabled={!selected.length} onClick={apply}>Apply selected organisation suggestions</Button>
    </fieldset>}
  </section>;
}
